import { generateAppearance } from "@unit/appearance-core";

const THEME_STORAGE_KEY = "isDarkMode";
const THEME_META_COLOURS = Object.freeze({
  light: "#f0f0f5",
  dark: "#2d2d2d",
});

const ACCENT_TOKEN_NAMES = Object.freeze([
  "accent-primary",
  "accent-emphasis",
  "accent-selection",
  "accent-surface",
  "accent-surface-subtle",
  "text-on-accent",
  "text-on-accent-selection",
  "text-on-accent-subtle",
]);

let currentMode = "dark";
let currentAppearance = null;
let unitReferenceForced = false;

const rootElement = () =>
  typeof document === "undefined" ? null : document.documentElement;

const cssTokenName = (name) =>
  name.startsWith("--") ? name : `--color-${name}`;

const themeTokens = (appearance, mode) =>
  appearance?.themes?.[mode]?.tokens || null;

function writeThemeMode(mode) {
  const root = rootElement();
  if (!root) return;
  root.setAttribute("data-theme", mode);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_META_COLOURS[mode]);
}

function clearAccentProperties() {
  const root = rootElement();
  if (!root) return;
  ACCENT_TOKEN_NAMES.forEach((name) => {
    root.style.removeProperty(cssTokenName(name));
  });
}

function writeAppearanceTokens() {
  clearAccentProperties();
  if (unitReferenceForced || !currentAppearance) return;

  const tokens = themeTokens(currentAppearance, currentMode);
  if (!tokens) return;
  ACCENT_TOKEN_NAMES.forEach((name) => {
    const value = tokens[name] || tokens[cssTokenName(name)];
    if (typeof value === "string" && value) {
      rootElement()?.style.setProperty(cssTokenName(name), value);
    }
  });
}

function applyRuntimeState() {
  writeThemeMode(currentMode);
  writeAppearanceTokens();
}

export function readStoredThemePreference(storage = globalThis.localStorage) {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY);
    if (value === "true") return "dark";
    if (value === "false") return "light";
  } catch (_error) {
    // Browser storage is an enhancement, not an authority source.
  }
  return null;
}

export function initializeAppearanceRuntime({ prefersDark = true } = {}) {
  currentMode = readStoredThemePreference() || (prefersDark ? "dark" : "light");
  applyRuntimeState();
  return currentMode;
}

export function setAppearanceMode(mode, { persist = true } = {}) {
  if (!Object.prototype.hasOwnProperty.call(THEME_META_COLOURS, mode)) {
    throw new TypeError("Appearance mode must be light or dark.");
  }
  currentMode = mode;
  if (persist) {
    try {
      globalThis.localStorage?.setItem(THEME_STORAGE_KEY, String(mode === "dark"));
    } catch (_error) {
      // Keep the live preference even if browser storage is unavailable.
    }
  }
  applyRuntimeState();
  return currentMode;
}

export function applyGeneratedAppearance(appearance) {
  currentAppearance = appearance || null;
  applyRuntimeState();
}

export function applyAccentSeed(accentSeed) {
  currentAppearance = accentSeed ? generateAppearance(accentSeed) : null;
  applyRuntimeState();
  return currentAppearance;
}

export function clearOrganisationAppearance() {
  currentAppearance = null;
  applyRuntimeState();
}

export function forceUnitReferenceAppearance(force = true) {
  unitReferenceForced = force === true;
  applyRuntimeState();
}

export function appearanceRuntimeSnapshot() {
  return Object.freeze({
    mode: currentMode,
    hasOrganisationAppearance: Boolean(currentAppearance),
    unitReferenceForced,
  });
}

export const appearanceRuntime = Object.freeze({
  initialize: initializeAppearanceRuntime,
  setMode: setAppearanceMode,
  applyGenerated: applyGeneratedAppearance,
  applySeed: applyAccentSeed,
  clear: clearOrganisationAppearance,
  forceUnitReference: forceUnitReferenceAppearance,
  snapshot: appearanceRuntimeSnapshot,
});
